import { useCallback, useEffect, useState } from 'react'
import { FiChevronLeft, FiChevronRight, FiSearch } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { useDebounce } from '@/hooks/useDebounce'
import { getUser } from '@/utils/auth'
import SalesforceSyncButton from '@/components/common/SalesforceSyncButton'
import PageSizeSelect from '@/components/common/PageSizeSelect'
import StockReferenceSelectInput from '@/components/inventory/StockReferenceSelectInput'
import {
  assignSalesforceOrderLot,
  getSalesforceOrders,
  SalesforceOrder,
  updateSalesforceOrderOutcome,
} from '@/services/api/salesforce'
import {
  startSalesforceSync,
  subscribeSalesforceSync,
  watchSalesforceSyncInBackground,
  type SalesforceSyncState,
} from '@/services/salesforceSync'

const money = (value: string | number | null | undefined, currency = 'ZAR') => {
  if (value == null || value === '') return '—'
  const amount = Number(value)
  if (!Number.isFinite(amount)) return String(value)
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: currency || 'ZAR',
    maximumFractionDigits: 2,
  }).format(amount)
}

const date = (value: string | null | undefined) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—'

const qcLabel = (order: SalesforceOrder) => {
  if (order.orderFulfillment?.outcome === 'Accepted') return 'QC accepted'
  if (order.orderFulfillment?.outcome === 'Rejected') return 'QC rejected'
  return 'Awaiting QC'
}

const isPlaceholderLot = (lot?: string | null) => {
  const value = String(lot ?? '').trim()
  if (!value) return true
  return /^(test|n\/?a|none|tbd|todo|123|0+)$/i.test(value) || /^\d{1,3}$/.test(value)
}

const orderLot = (order: SalesforceOrder) => order.lot || order.orderFulfillment?.lotBatch || ''

const qcStyle = (order: SalesforceOrder) => {
  if (order.orderFulfillment?.outcome === 'Accepted') {
    return { color: '#137A43', background: '#E8F7EF' }
  }
  if (order.orderFulfillment?.outcome === 'Rejected') {
    return { color: '#B42318', background: '#FDEBE9' }
  }
  return { color: '#8A6100', background: '#FFF7D6' }
}

const OrderFulfillment = () => {
  const role = getUser()?.role
  const canManage = role === 'admin' || role === 'operations_manager'
  const [orders, setOrders] = useState<SalesforceOrder[]>([])
  const [page, setPage] = useState(1)
  const [resultsPerPage, setResultsPerPage] = useState(25)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const query = useDebounce(search, 350)
  const [outcome, setOutcome] = useState<'' | 'Pending' | 'Accepted' | 'Rejected'>('')
  const [loading, setLoading] = useState(true)
  const [syncJob, setSyncJob] = useState<SalesforceSyncState>({
    status: 'idle',
    fetched: 0,
    processed: 0,
    upserted: 0,
    since: '2026-04-01',
    percent: 0,
  })
  const [updatingId, setUpdatingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getSalesforceOrders({
        page,
        limit: resultsPerPage,
        search: query || undefined,
        outcome: outcome || undefined,
      })
      setOrders(result.orders)
      setTotal(result.total)
      setTotalPages(Math.max(result.totalPages, 1))
    } catch (error: any) {
      toast.error(error?.message || 'Could not load Salesforce Orders.')
    } finally {
      setLoading(false)
    }
  }, [outcome, page, query, resultsPerPage])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    setPage(1)
  }, [query, outcome])

  useEffect(() => {
    watchSalesforceSyncInBackground()
    return subscribeSalesforceSync((job) => {
      setSyncJob(job)
      if (job.status === 'completed') {
        void load()
      }
    })
  }, [load])

  const handleSync = async () => {
    if (syncJob.status === 'running') return
    try {
      await startSalesforceSync()
    } catch (error: any) {
      toast.error(error?.message || 'Could not start Salesforce Order sync.')
    }
  }

  const handleLot = async (order: SalesforceOrder, lot: string) => {
    if (!lot || lot === orderLot(order)) return
    setUpdatingId(order.id)
    try {
      const result = await assignSalesforceOrderLot(order.id, lot)
      toast.success(
        result.salesforceUpdated
          ? `Zacpak lot ${result.lot} is on the order. Sync Flyte to attach unique trucks.`
          : `Lot ${result.lot} saved in Connect. Reconnect Salesforce if Lot__c did not update.`
      )
      await load()
    } catch (error: any) {
      toast.error(error?.message || 'Could not set Zacpak lot.')
    } finally {
      setUpdatingId(null)
    }
  }

  const handleOutcome = async (order: SalesforceOrder, next: string) => {
    const value = next === 'Rejected' ? 'Rejected' : null
    setUpdatingId(order.id)
    try {
      await updateSalesforceOrderOutcome(order.id, value)
      toast.success(
        value === 'Rejected'
          ? 'QC rejected — fulfillment remains open.'
          : 'QC rejection cleared. Fully paid will be accepted automatically.'
      )
      await load()
    } catch (error: any) {
      toast.error(error?.message || 'Could not update QC outcome.')
    } finally {
      setUpdatingId(null)
    }
  }

  return (
    <div className="p-6" style={{ fontFamily: 'Lexend Deca' }}>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="mb-1 text-xs" style={{ color: '#8B8B98' }}>
            Operations / Order Fulfillment
          </p>
          <h1 className="text-2xl font-semibold" style={{ color: '#212126' }}>
            Order Fulfillment
          </h1>
          <p className="mt-2 max-w-3xl text-sm" style={{ color: '#69697A' }}>
            Commercial orders come from Salesforce. Empty lots are picked from Zacpak
            inventory — not typed. Fully paid becomes QC accepted. Unpaid or partially paid
            return to awaiting QC. Rejected stays manual. Warehouse quantities stay in Zacpak.
          </p>
        </div>
        {canManage && (
          <SalesforceSyncButton
            job={syncJob}
            onClick={handleSync}
            idleLabel="Refresh from Salesforce"
          />
        )}
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border bg-white p-4" style={{ borderColor: '#E9E9EA' }}>
          <p className="text-xs" style={{ color: '#69697A' }}>Matching orders</p>
          <p className="mt-1 text-2xl font-semibold" style={{ color: '#212126' }}>{total}</p>
        </div>
        <div className="rounded-lg border bg-white p-4" style={{ borderColor: '#E9E9EA' }}>
          <p className="text-xs" style={{ color: '#69697A' }}>Commercial source</p>
          <p className="mt-1 text-sm font-semibold" style={{ color: '#00A1E0' }}>Salesforce</p>
        </div>
        <div className="rounded-lg border bg-white p-4" style={{ borderColor: '#E9E9EA' }}>
          <p className="text-xs" style={{ color: '#69697A' }}>Completion rule</p>
          <p className="mt-1 text-sm font-semibold" style={{ color: '#137A43' }}>Fully paid → QC accepted</p>
        </div>
      </div>

      <div className="rounded-lg border bg-white" style={{ borderColor: '#E9E9EA' }}>
        <div className="flex flex-wrap gap-3 border-b p-4" style={{ borderColor: '#E9E9EA' }}>
          <label className="relative min-w-[260px] flex-1">
            <FiSearch className="absolute left-3 top-3" style={{ color: '#8B8B98' }} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search order, KCG invoice, customer or product"
              className="h-10 w-full rounded-md border pl-9 pr-3 text-sm outline-none"
              style={{ borderColor: '#D9D9DE' }}
            />
          </label>
          <select
            value={outcome}
            onChange={(event) => setOutcome(event.target.value as typeof outcome)}
            className="h-10 rounded-md border bg-white px-3 text-sm"
            style={{ borderColor: '#D9D9DE', color: '#212126' }}
          >
            <option value="">All QC states</option>
            <option value="Pending">Awaiting QC</option>
            <option value="Accepted">QC accepted</option>
            <option value="Rejected">QC rejected</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead style={{ background: '#F7F8F7', color: '#69697A' }}>
              <tr>
                <th className="px-4 py-3 font-medium">Salesforce Order</th>
                <th className="px-4 py-3 font-medium">KCG invoice</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Zacpak lot</th>
                <th className="px-4 py-3 font-medium">Order date</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Payment</th>
                <th className="px-4 py-3 font-medium">Salesforce</th>
                <th className="px-4 py-3 font-medium">Fulfillment / QC</th>
                <th className="px-4 py-3 font-medium">Warehouse link</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center" style={{ color: '#69697A' }}>Loading orders…</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center" style={{ color: '#69697A' }}>No Salesforce Orders match this view.</td></tr>
              ) : (
                orders.map((order) => {
                  const dispatches = order.orderFulfillment?.stockDispatches || []
                  const qc = qcStyle(order)
                  return (
                    <tr key={order.id} className="border-t" style={{ borderColor: '#EFEFF1' }}>
                      <td className="whitespace-nowrap px-4 py-3 font-medium" style={{ color: '#212126' }}>
                        {order.orderNumber || '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">{order.xeroInvoiceId || '—'}</td>
                      <td className="min-w-[220px] px-4 py-3">{order.accountName || '—'}</td>
                      <td className="min-w-[240px] px-4 py-3">
                        {canManage && isPlaceholderLot(orderLot(order)) ? (
                          <StockReferenceSelectInput
                            type="lot"
                            value=""
                            onChange={(lot) => {
                              void handleLot(order, lot)
                            }}
                          />
                        ) : (
                          <span className="text-xs" style={{ color: '#212126' }}>
                            {orderLot(order) || '—'}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">{date(order.effectiveDate)}</td>
                      <td className="whitespace-nowrap px-4 py-3">{money(order.invoiceAmount, order.currencyIsoCode || 'ZAR')}</td>
                      <td className="whitespace-nowrap px-4 py-3">{order.paymentStatus || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full px-2 py-1 text-xs" style={{ color: '#555563', background: '#F2F4F2' }}>
                          {order.status || 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {order.orderFulfillment?.outcome === 'Accepted' || !canManage ? (
                          <span className="rounded-full px-2 py-1 text-xs font-medium" style={qc}>{qcLabel(order)}</span>
                        ) : (
                          <select
                            value={order.orderFulfillment?.outcome || ''}
                            disabled={updatingId === order.id}
                            onChange={(event) => handleOutcome(order, event.target.value)}
                            className="rounded-md border px-2 py-1.5 text-xs font-medium disabled:opacity-50"
                            style={{ borderColor: '#D9D9DE', ...qc }}
                          >
                            <option value="">Awaiting QC</option>
                            <option value="Rejected">QC rejected</option>
                          </select>
                        )}
                      </td>
                      <td className="min-w-[170px] px-4 py-3 text-xs" style={{ color: '#69697A' }}>
                        {dispatches.length
                          ? `${dispatches.length} dispatch${dispatches.length === 1 ? '' : 'es'}`
                          : 'Not dispatched'}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t px-4 py-3" style={{ borderColor: '#E9E9EA' }}>
          <span className="text-xs" style={{ color: '#69697A' }}>
            Page {page} of {totalPages} · {total} orders
          </span>
          <div className="flex items-center gap-2">
            <PageSizeSelect
              value={resultsPerPage}
              onChange={(size) => {
                setResultsPerPage(size)
                setPage(1)
              }}
            />
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((value) => value - 1)}
              className="rounded-md border p-2 disabled:opacity-40"
              style={{ borderColor: '#D9D9DE' }}
              aria-label="Previous page"
            >
              <FiChevronLeft />
            </button>
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((value) => value + 1)}
              className="rounded-md border p-2 disabled:opacity-40"
              style={{ borderColor: '#D9D9DE' }}
              aria-label="Next page"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderFulfillment
